// NOTE: This file is stable and usually should not be modified.
// It is important that all functionality in this file is preserved, and should only be modified if explicitly requested.

import { ChevronDown, LogOut, UserIcon, UserPlus, Wallet } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu.tsx';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar.tsx';
import { WalletModal } from '@/components/WalletModal';
import { useLoggedInAccounts, type Account } from '@/hooks/useLoggedInAccounts';
import { genUserName } from '@/lib/genUserName';

interface AccountSwitcherProps {
  onAddAccountClick: () => void;
}

export function AccountSwitcher({ onAddAccountClick }: AccountSwitcherProps) {
  const { currentUser, otherUsers, setLogin, removeLogin } = useLoggedInAccounts();

  if (!currentUser) return null;

  const getDisplayName = (account: Account): string => {
    return account.metadata.name ?? genUserName(account.pubkey);
  }

  return (
    <DropdownMenu modal={false}>
      <DropdownMenuTrigger asChild>
        <button className='group flex items-center gap-2 py-1.5 px-1.5 pr-3 rounded-full bg-secondary/60 hover:bg-secondary border border-border/40 hover:border-border/60 transition-all text-foreground shadow-sm'>
          <Avatar className='w-7 h-7 ring-2 ring-background'>
            <AvatarImage src={currentUser.metadata.picture} alt={getDisplayName(currentUser)} />
            <AvatarFallback className='text-xs bg-primary/10 text-primary font-medium'>{getDisplayName(currentUser).charAt(0).toUpperCase()}</AvatarFallback>
          </Avatar>
          <span className='text-sm font-medium truncate max-w-[100px] hidden sm:block'>{getDisplayName(currentUser)}</span>
          <ChevronDown className='w-3.5 h-3.5 text-muted-foreground group-hover:text-foreground transition-colors' />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent className='w-52 p-1.5 rounded-xl border-border/50 shadow-xl shadow-black/10 animate-scale-in'>
        {otherUsers.length > 0 && (
          <>
            <div className='font-medium text-xs text-muted-foreground px-2 py-1.5 uppercase tracking-wide'>Switch Account</div>
            {otherUsers.map((user) => (
              <DropdownMenuItem
                key={user.id}
                onClick={() => setLogin(user.id)}
                className='flex items-center gap-2.5 cursor-pointer py-2 px-2 rounded-lg'
              >
                <Avatar className='w-7 h-7'>
                  <AvatarImage src={user.metadata.picture} alt={getDisplayName(user)} />
                  <AvatarFallback className='text-xs'>{getDisplayName(user)?.charAt(0) || <UserIcon className='w-3 h-3' />}</AvatarFallback>
                </Avatar>
                <span className='text-sm font-medium truncate'>{getDisplayName(user)}</span>
              </DropdownMenuItem>
            ))}
            <DropdownMenuSeparator className='my-1.5' />
          </>
        )}
        <WalletModal>
          <DropdownMenuItem
            className='flex items-center gap-2.5 cursor-pointer py-2 px-2 rounded-lg text-sm'
            onSelect={(e) => e.preventDefault()}
          >
            <Wallet className='w-4 h-4 text-muted-foreground' />
            <span>Wallet</span>
          </DropdownMenuItem>
        </WalletModal>
        <DropdownMenuItem
          onClick={onAddAccountClick}
          className='flex items-center gap-2.5 cursor-pointer py-2 px-2 rounded-lg text-sm'
        >
          <UserPlus className='w-4 h-4 text-muted-foreground' />
          <span>Add account</span>
        </DropdownMenuItem>
        <DropdownMenuSeparator className='my-1.5' />
        <DropdownMenuItem
          onClick={() => removeLogin(currentUser.id)}
          className='flex items-center gap-2.5 cursor-pointer py-2 px-2 rounded-lg text-sm text-red-500 focus:text-red-500 focus:bg-red-500/10'
        >
          <LogOut className='w-4 h-4' />
          <span>Log out</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}